import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import apiRequest from '@/services/apiRequest';
import { IBookingService } from '@/services/types/booking/services';
import { getCoreApi } from '@/utility/getCoreApi';

export const useAvailableServices = (
  bookingId?: string | number,
  query: Record<string, string> = {},
) => {
  const normalizedBookingId = bookingId ? String(bookingId) : null;
  const servicesCacheKey = normalizedBookingId
    ? `bookings/${normalizedBookingId}/services?${new URLSearchParams(query)}`
    : null;

  const fetcher = (key: string) =>
    apiRequest<IBookingService[]>({ url: `${getCoreApi()}/${key}` }, true);

  const updateFetcher = async (_key: string, { arg }: { arg: IBookingService[] }) => {
    const url = `${getCoreApi()}/bookings/${normalizedBookingId}/services`;
    return apiRequest<IBookingService[]>(
      {
        url,
        method: 'POST',
        data: { skip_required_fields_validation: true, data: arg },
      },
      true,
    );
  };

  const addServiceFetcher = async (_key: string, { arg }: { arg: { serviceId: number } }) => {
    const url = `${getCoreApi()}/bookings/${normalizedBookingId}/services/${arg.serviceId}`;
    return apiRequest<IBookingService>(
      {
        url,
        method: 'POST',
      },
      true,
    );
  };

  const removeServiceFetcher = async (_key: string, { arg }: { arg: { serviceId: number } }) => {
    const url = `${getCoreApi()}/bookings/${normalizedBookingId}/services/${arg.serviceId}`;
    return apiRequest<void>(
      {
        url,
        method: 'DELETE',
      },
      true,
    );
  };

  const {
    data,
    error: fetchError,
    isLoading,
    isValidating,
    mutate: revalidate,
  } = useSWR<IBookingService[]>(servicesCacheKey, fetcher, {
    revalidateOnMount: true,
    revalidateIfStale: false,
    revalidateOnFocus: false,
  });

  const {
    trigger: updateServices,
    isMutating: isUpdating,
    error: updateError,
    data: updateData,
  } = useSWRMutation(servicesCacheKey, updateFetcher, {
    revalidate: true,
  });

  const {
    trigger: addServiceMutation,
    isMutating: isAddingService,
    error: addServiceError,
    data: addServiceData,
  } = useSWRMutation(`${servicesCacheKey}-add`, addServiceFetcher, {
    revalidate: true,
  });

  const {
    trigger: removeServiceMutation,
    isMutating: isRemovingService,
    error: removeServiceError,
    data: removeServiceData,
  } = useSWRMutation(`${servicesCacheKey}-remove`, removeServiceFetcher, {
    revalidate: true,
  });

  const addService = (serviceId: number) => addServiceMutation({ serviceId });
  const removeService = (serviceId: number) => removeServiceMutation({ serviceId });

  return {
    data,
    isLoading,
    isValidating,
    fetchError,
    revalidate,

    updateServices,
    isUpdating,
    updateError,
    updateData,

    addService,
    isAddingService,
    addServiceError,
    addServiceData,

    removeService,
    isRemovingService,
    removeServiceError,
    removeServiceData,
  };
};
